import type { CSSProperties } from "react";
import type { Theme } from "./pixel";

// Style helpers ported from Claude Design's dotfile. They return plain
// style objects so we can spread them into JSX `style` props. Only the
// `bitmap` / `pixel` branches are hot paths right now (pixel is the only
// theme), but the other branches are kept for an easy future multi-theme.

export function panelStyle(theme: Theme): CSSProperties {
  const base: CSSProperties = {
    background: theme.panel,
    color: theme.ink,
    borderRadius: theme.radius.panel,
  };
  if (theme.panelShape === "bitmap") {
    return {
      ...base,
      background: theme.panelSolid,
      border: `2px solid ${theme.panelEdgeStrong}`,
      boxShadow: `
        inset 0 0 0 2px ${theme.bgDeep},
        inset 0 0 0 4px ${theme.panelEdge},
        0 0 0 2px ${theme.bgDeep},
        4px 4px 0 0 ${theme.bgDeep}
      `,
      imageRendering: "pixelated",
    };
  }
  if (theme.panelShape === "framed") {
    return {
      ...base,
      background: theme.panelSolid,
      border: `1px solid ${theme.panelEdgeStrong}`,
      outline: `1px solid ${theme.panelEdgeStrong}`,
      outlineOffset: 3,
    };
  }
  if (theme.panelShape === "riveted") {
    return {
      ...base,
      border: `1px solid ${theme.panelEdge}`,
      boxShadow: `inset 0 0 0 1px rgba(255, 200, 140, 0.08),
                  inset 0 1px 0 rgba(255, 180, 100, 0.18),
                  inset 0 -1px 0 rgba(0, 0, 0, 0.5),
                  0 0 24px rgba(255, 90, 20, 0.12)`,
    };
  }
  // soft
  return {
    ...base,
    border: `1px solid ${theme.panelEdge}`,
    backdropFilter: "blur(12px)",
  };
}

export function dividerStyle(theme: Theme): CSSProperties {
  if (theme.divider === "dashed-pixel") {
    return {
      height: 2,
      background: "transparent",
      border: "none",
      backgroundImage: `linear-gradient(90deg, ${theme.panelEdgeStrong} 50%, transparent 50%)`,
      backgroundSize: "8px 2px",
      backgroundRepeat: "repeat-x",
    };
  }
  if (theme.divider === "hairline") {
    return { height: 1, background: theme.panelEdgeStrong, border: "none" };
  }
  // forged
  return {
    height: 3,
    background: "transparent",
    borderTop: `1px solid ${theme.panelEdge}`,
    borderBottom: `1px solid ${theme.panelEdge}`,
  };
}

// 4px ink-coloured offset shadow — the bitmap drop-shadow seen on
// inventory-page cards, dialog modals, and shop tiles. Same formula
// as `simplePanelStyle`. Spread alongside any card-shaped surface;
// replace its existing `boxShadow` (this is a complete shadow on its
// own, not a merge).
//
// Imported from `theme/endesga32`'s `E32.ink` lazily so callers don't
// need the palette import.
import { E32 } from "./endesga32";

export const bevelShadow = `4px 4px 0 0 ${E32.ink}`;

export function glowShadow(theme: Theme, color: string, intensity = 1): string {
  if (theme.glow === "none") return "none";
  // ember
  return `0 0 ${12 * intensity}px ${color}, 0 0 ${4 * intensity}px ${color}`;
}

export function uiText(
  theme: Theme,
  opts: { size?: number; dim?: boolean } = {},
): CSSProperties {
  const { size = 10, dim = false } = opts;
  return {
    fontFamily: theme.fontBody,
    fontSize: size,
    letterSpacing: theme.letterSpacing,
    textTransform: theme.uppercase ? "uppercase" : "none",
    fontVariant: theme.uppercase ? "normal" : "small-caps",
    color: dim ? theme.inkFaint : theme.inkDim,
  };
}

// Button system mirrors components_buttons.html. All three variants
// share Press Start 2P / uppercase / 10 px / 0.08em chrome. They differ
// only in size and surface:
//   primary  · 8 / 12  · ruby-on-parchment      (the one CTA per region)
//   default  · 8 / 12  · panel-face + panel-edge (in-row actions: PLAY, EDIT, DELETE)
//   ghost    · 6 / 10  · panel-face + panel-edge (header / utility chrome: LOG OUT, COLLECTION)
// Hover snaps a 2 px ink bitmap shadow on, press snaps it back off and
// nudges 1 px down-right. There is no transition — bitmap UI snaps.
// Hover/press behavior lives in the global `.doi-btn` CSS rules emitted
// by buildRootCss (theme/endesga32.ts), so callers must spread BOTH the
// `style` and `className` returned here.

export interface BtnProps {
  style: CSSProperties;
  className: string;
}

function buttonBase(theme: Theme): CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    textTransform: theme.uppercase ? "uppercase" : "none",
    cursor: "pointer",
    borderRadius: theme.radius.button,
    transition: "none",
  };
}

export function primaryBtn(theme: Theme, disabled = false): BtnProps {
  return {
    className: "doi-btn doi-btn-primary",
    style: {
      ...buttonBase(theme),
      padding: "8px 12px",
      background: theme.accent,
      color: theme.bgDeep,
      border: `2px solid ${theme.accent}`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    },
  };
}

export function defaultBtn(theme: Theme, disabled = false): BtnProps {
  return {
    className: "doi-btn doi-btn-default",
    style: {
      ...buttonBase(theme),
      padding: "8px 12px",
      background: theme.panelSolid,
      color: theme.inkDim,
      border: `2px solid ${theme.panelEdge}`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    },
  };
}

/** Merge a style override into a `BtnProps` while preserving the
 *  `className` (so hover/press still works). */
export function withBtnStyle(props: BtnProps, override: CSSProperties): BtnProps {
  return { className: props.className, style: { ...props.style, ...override } };
}

export function ghostBtn(theme: Theme, disabled = false): BtnProps {
  return {
    className: "doi-btn doi-btn-ghost",
    style: {
      ...buttonBase(theme),
      padding: "6px 10px",
      background: theme.panelSolid,
      color: theme.inkDim,
      border: `2px solid ${theme.panelEdge}`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    },
  };
}
